import React, { useState, useEffect } from 'react';
import {
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  ActivityIndicator,
  Alert,
  SafeAreaView,
  Platform
} from 'react-native';
import { paymobService } from '../services/paymob.service';
import { colors, typography, spacing } from '../theme';
import { useAppContext } from '../contexts/AppContext';
import { useAppStore } from '../store/appStore';
import { Logo, IOSRestrictionMessage } from '../components';
import { Ionicons } from '@expo/vector-icons';
import { DirectionalIcon } from '../components/DirectionalIcon';
import { MoreOptionsModal } from '../components/MoreOptionsModal';
import { apiService } from '../services/api';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { useThemedStyles } from '../hooks/useThemedStyles';

// Conditional WebView import - only for native platforms
let WebView: any = null;
if (Platform.OS !== 'web') {
  WebView = require('react-native-webview').WebView;
}

// Global payment params (set before navigating to payment screen)
let paymentParams: any = null;

export function setPaymentParams(params: any) {
  paymentParams = params;
}

export const PaymentScreen: React.FC = () => {
  const styles = useThemedStyles(getStyles);
  const { navigateTo, showNotification } = useAppContext();
  const { user, logout } = useAppStore();
  
  const [amount, setAmount] = useState(paymentParams?.amount || 0);
  const [currency, setCurrency] = useState(paymentParams?.currency || 'USD');
  const [description, setDescription] = useState(paymentParams?.description || '');
  const [onSuccessCallback] = useState(() => paymentParams?.onSuccess);
  const [merchantOrderId, setMerchantOrderId] = useState(() => {
    // Generate merchant order ID from payment params
    if (paymentParams?.planId && paymentParams?.businessId) {
      return `SUB_${paymentParams.businessId}_${paymentParams.planId}_${Date.now()}`;
    }
    return null;
  });

  useEffect(() => {
    // Load payment params
    if (paymentParams) {
      setAmount(paymentParams.amount);
      setCurrency(paymentParams.currency || 'USD');
      setDescription(paymentParams.description);
    }

    // Check if returning from payment callback (web only)
    if (Platform.OS === 'web' && typeof window !== 'undefined') {
      const urlParams = new URLSearchParams(window.location.search);
      const paymentStatus = urlParams.get('payment');
      
      if (paymentStatus === 'success') {
        handlePaymentSuccess();
        // Clean URL
        window.history.replaceState({}, '', window.location.pathname);
      } else if (paymentStatus === 'failed') {
        handlePaymentFailure();
        // Clean URL
        window.history.replaceState({}, '', window.location.pathname);
      }
    }
  }, []);
  
  const [loading, setLoading] = useState(true);
  const [paymentUrl, setPaymentUrl] = useState<string | null>(null);
  const [showWebView, setShowWebView] = useState(false);
  const [showMoreModal, setShowMoreModal] = useState(false);

  const openMoreMenu = () => setShowMoreModal(true);
  const closeMoreMenu = () => setShowMoreModal(false);

  const renderFloatingMoreButton = (variant: 'light' | 'dark' = 'light') => (
    <TouchableOpacity
      style={[
        styles.moreButton,
        variant === 'dark' && styles.moreButtonDark
      ]}
      onPress={openMoreMenu}
      activeOpacity={0.7}
    >
      <Ionicons
        name="ellipsis-vertical"
        size={18}
        color={variant === 'dark' ? colors.white : colors.primary}
      />
    </TouchableOpacity>
  );

  // Auto-start payment when screen loads
  useEffect(() => {
    if (user && amount > 0) {
      initiatePayment();
    } else {
      setLoading(false);
      showNotification('error', 'Error', 'Missing payment information');
      navigateTo('subscription');
    }
  }, []);

  const initiatePayment = async () => {
    try {
      if (!user) {
        showNotification('error', 'Error', 'User information not found. Please login again.');
        navigateTo('unified-login');
        return;
      }

      if (!amount || amount <= 0) {
        showNotification('error', 'Error', 'Invalid payment amount');
        navigateTo('subscription');
        return;
      }

      setLoading(true);

      console.log('[PaymentScreen] Initiating payment with user data:', {
        firstName: user.firstName,
        lastName: user.lastName,
        email: user.email,
        phone: user.phone,
        amount
      });

      // Create payment intention using user data from token
      const response = await paymobService.createPaymentIntention({
        amount: amount,
        currency: currency, // Use currency from subscription plan
        firstName: user.firstName || 'Customer',
        lastName: user.lastName || 'User',
        email: user.email,
        phoneNumber: user.phone || '+20',
        description: description || 'Payment',
        merchantOrderId: merchantOrderId || undefined, // Pass merchant order ID
      });

      console.log('[PaymentScreen] Payment intention created, opening iframe');

      // Open payment WebView immediately
      setPaymentUrl(response.iframe_url);
      setShowWebView(true);

    } catch (error: any) {
      console.error('[PaymentScreen] Payment error:', error);
      showNotification('error', 'Error', error.message || 'Failed to initiate payment');
      navigateTo('subscription');
    } finally {
      setLoading(false);
    }
  };

  // Handle payment success
  const handlePaymentSuccess = async () => {
    console.log('[PaymentScreen] ========== PAYMENT SUCCESS HANDLER CALLED ==========');
    setShowWebView(false);
    
    console.log('[PaymentScreen] ✅ Payment successful - forcing logout to refresh subscription data');
    
    try {
      // Step 1: Wait a moment for webhook to process payment and create subscription
      console.log('[PaymentScreen] Step 1: Waiting for webhook to process payment...');
      await new Promise(resolve => setTimeout(resolve, 2000)); // Wait 2 seconds for webhook
      
      // Step 2: Call success callback if provided
      if (onSuccessCallback) {
        console.log('[PaymentScreen] Step 2: Calling success callback...');
        try {
          await onSuccessCallback();
          console.log('[PaymentScreen] ✅ Success callback completed');
        } catch (error) {
          console.error('[PaymentScreen] ❌ Error in success callback:', error);
        }
      }
      
      // Step 3: Show success notification
      showNotification('success', 'Payment Successful', 'Your subscription has been activated! Please login again to continue.');
      
      // Step 4: Force logout to clear old tokens and require fresh login
      console.log('[PaymentScreen] Step 4: Logging out to force fresh login with updated subscription...');
      
      // Clear API service tokens FIRST (before store logout)
      apiService.setAuthToken(null);
      apiService.setRefreshToken(null);
      console.log('[PaymentScreen] ✅ API service tokens cleared');
      
      // Clear all tokens and auth state in store
      logout();
      console.log('[PaymentScreen] ✅ Store logout completed');
      
      // Clear AsyncStorage
      try {
        await AsyncStorage.removeItem('sendifier-app-store');
        await AsyncStorage.removeItem('@sendifier:auth:backup');
        console.log('[PaymentScreen] ✅ AsyncStorage cleared');
      } catch (storageError) {
        console.error('[PaymentScreen] Error clearing storage:', storageError);
      }
      
      // Navigate to login screen immediately
      // On web, use window.location for a hard redirect to ensure navigation happens
      if (Platform.OS === 'web' && typeof window !== 'undefined') {
        // Force full page reload to login
        window.location.href = '/unified-login';
      } else {
        // On mobile, use normal navigation
        navigateTo('unified-login');
      }
    } catch (error: any) {
      console.error('[PaymentScreen] ❌ ERROR in handlePaymentSuccess:', error);
      console.error('[PaymentScreen] Error message:', error?.message);
      console.error('[PaymentScreen] Error stack:', error?.stack);
      
      // Show notification and force logout
      showNotification('success', 'Payment Successful', 'Payment completed. Please login again to see your updated subscription.');
      
      // Clear API service tokens FIRST
      apiService.setAuthToken(null);
      apiService.setRefreshToken(null);
      
      // Clear all tokens and auth state in store
      logout();
      
      // Clear AsyncStorage
      try {
        await AsyncStorage.removeItem('sendifier-app-store');
        await AsyncStorage.removeItem('@sendifier:auth:backup');
      } catch (storageError) {
        console.error('[PaymentScreen] Error clearing storage:', storageError);
      }
      
      // Navigate to login screen immediately
      // On web, use window.location for a hard redirect
      if (Platform.OS === 'web' && typeof window !== 'undefined') {
        // Force full page reload to login
        window.location.href = '/unified-login';
      } else {
        // On mobile, use normal navigation
        navigateTo('unified-login');
      }
    }
  };

  // Handle payment failure
  const handlePaymentFailure = (errorMessage?: string) => {
    setShowWebView(false);
    const errorMsg = errorMessage || 'Payment was not completed. Please check your card details and try again.';
    showNotification('error', 'Payment Failed', errorMsg);
    setTimeout(() => {
      navigateTo('subscription');
    }, 1000);
  };

  // Extract error message from URL
  const extractErrorMessageFromUrl = (url: string): string | undefined => {
    try {
      const urlObj = new URL(url);
      const errorMsg = urlObj.searchParams.get('data.message');
      const errorOccurred = urlObj.searchParams.get('error_occured');
      const txnResponseCode = urlObj.searchParams.get('txn_response_code');
      const is3DSecure = urlObj.searchParams.get('is_3d_secure');
      
      // Check for 3DS authentication failure (DECLINED + is_3d_secure)
      if (txnResponseCode === 'DECLINED' && is3DSecure === 'true') {
        return '3D Secure Authentication Failed. Please try again or use a different card.';
      }
      
      // Check for declined transaction
      if (txnResponseCode === 'DECLINED') {
        return 'Transaction was declined. Please check your card details or try a different payment method.';
      }
      
      // Map common Paymob response codes to user-friendly messages
      const errorCodes: Record<string, string> = {
        '14': 'Invalid Card Number',
        '15': 'Invalid Expiry Date',
        '16': 'Invalid CVV',
        '17': 'Insufficient Funds',
        '18': 'Card Expired',
        '19': 'Card Declined',
        '20': 'Transaction Declined',
        '21': 'Invalid PIN',
        '22': 'Card Not Supported',
        '23': '3D Secure Authentication Failed',
        '24': 'Transaction Timeout'
      };
      
      if (errorMsg) {
        return decodeURIComponent(errorMsg.replace(/\+/g, ' '));
      }
      
      if (txnResponseCode && errorCodes[txnResponseCode]) {
        return errorCodes[txnResponseCode];
      }
      
      if (errorOccurred === 'true') {
        return 'Payment could not be processed. Please check your card details and try again.';
      }
      
      return undefined;
    } catch (e) {
      console.error('[PaymentScreen] Error extracting error message from URL:', e);
      return undefined;
    }
  };

  // For native WebView navigation
  const handleWebViewNavigationStateChange = async (navState: any) => {
    const { url } = navState;

    console.log('[PaymentScreen] WebView navigated to:', url);

    // Check for DECLINED transactions first (including 3DS failures) - takes precedence
    if (url.includes('txn_response_code=DECLINED')) {
      console.log('[PaymentScreen] Payment DECLINED detected from URL:', url);
      const errorMsg = extractErrorMessageFromUrl(url);
      handlePaymentFailure(errorMsg);
      return;
    }

    // Check if error occurred
    if (url.includes('error_occured=true') || url.includes('error_occurred=true')) {
      console.log('[PaymentScreen] Payment ERROR detected from URL:', url);
      const errorMsg = extractErrorMessageFromUrl(url);
      handlePaymentFailure(errorMsg);
      return;
    }
    
    // Check for Paymob success patterns
    // Payment is successful if success=true AND error_occured is NOT true AND not DECLINED
    if ((url.includes('success=true') && !url.includes('error_occured=true') && !url.includes('DECLINED')) || 
        (url.includes('/success') && !url.includes('error_occured=true') && !url.includes('DECLINED')) || 
        url.includes('txn_response_code=APPROVED')) {
      console.log('[PaymentScreen] Payment SUCCESS detected from URL:', url);
      await handlePaymentSuccess();
      return;
    }
    
    // Check for other Paymob failure patterns
    if (url.includes('success=false') || 
        url.includes('/failure') || 
        url.includes('/error') ||
        url.includes('?failed') ||
        url.includes('payment-callback.html')) {
      console.log('[PaymentScreen] Payment FAILURE detected from URL:', url);
      const errorMsg = extractErrorMessageFromUrl(url);
      handlePaymentFailure(errorMsg);
      return;
    }

    // Log all URL changes for debugging
    console.log('[PaymentScreen] URL change detected (no action taken):', url);
  };

  // Listen for postMessage from iframe (for web platform) and WebView (for React Native)
  useEffect(() => {
    // Web platform - window.postMessage
    if (Platform.OS === 'web') {
      const handleMessage = (event: MessageEvent) => {
        console.log('[PaymentScreen] Received message:', event.data);
        // Listen for Paymob success/failure messages
        if (event.data?.type === 'paymob_success') {
          console.log('[PaymentScreen] Payment success message received, clearing token and redirecting to login');
          handlePaymentSuccess();
        } else if (event.data?.type === 'paymob_failure') {
          const errorMsg = event.data?.error || event.data?.errorMessage;
          handlePaymentFailure(errorMsg);
        }
      };

      window.addEventListener('message', handleMessage);
      return () => window.removeEventListener('message', handleMessage);
    }
    
    // React Native - WebView onMessage
    if (Platform.OS !== 'web' && WebView) {
      // This will be handled by the onMessage prop of WebView component
      // See WebView component below
    }
  }, [showWebView]);

  // Show iOS restriction message on iOS
  if (Platform.OS === 'ios') {
    return (
      <SafeAreaView style={styles.container}>
        <IOSRestrictionMessage
          title="Payment Not Available"
          message="Payment processing is not available in this version of the app."
        />
        <View style={styles.iosBackContainer}>
          <TouchableOpacity
            style={styles.iosBackButton}
            onPress={() => navigateTo('subscription')}
          >
            <DirectionalIcon name="arrow-back" size={20} color={colors.white} />
            <Text style={styles.iosBackButtonText}>Back to Subscription</Text>
          </TouchableOpacity>
        </View>
      </SafeAreaView>
    );
  }

  // If showing payment WebView/iFrame
  if (showWebView && paymentUrl) {
    // For Web platform - use iframe
    if (Platform.OS === 'web') {
      return (
        <>
          <SafeAreaView style={styles.webViewContainer}>
            <View style={styles.webViewHeader}>
              <Logo size="sm" />
              <View style={styles.webViewHeaderActions}>
                <TouchableOpacity
                  style={styles.inlineMoreButton}
                  onPress={openMoreMenu}
                >
                  <Ionicons name="ellipsis-horizontal" size={20} color={colors.primary} />
                </TouchableOpacity>
                <TouchableOpacity
                  style={styles.cancelButton}
                  onPress={() => {
                    if (confirm('Are you sure you want to cancel this payment?')) {
                      setShowWebView(false);
                      navigateTo('subscription');
                    }
                  }}
                >
                  <Text style={styles.cancelButtonText}>✕ Cancel</Text>
                </TouchableOpacity>
              </View>
            </View>
          
            <View style={styles.webIframeWrapper}>
              <iframe
            ref={(iframe) => {
              if (iframe && typeof window !== 'undefined') {
                // Poll iframe URL to detect success/failure
                const checkInterval = setInterval(() => {
                  try {
                    const iframeUrl = iframe.contentWindow?.location?.href || '';
                    console.log('[PaymentScreen] Checking iframe URL:', iframeUrl);
                    
                    // Check for DECLINED transactions first (including 3DS failures)
                    if (iframeUrl.includes('txn_response_code=DECLINED')) {
                      console.log('[PaymentScreen] ❌ Payment DECLINED detected!');
                      clearInterval(checkInterval);
                      const errorMsg = extractErrorMessageFromUrl(iframeUrl);
                      handlePaymentFailure(errorMsg);
                    }
                    // Check if error occurred
                    else if (iframeUrl.includes('error_occured=true') || iframeUrl.includes('error_occurred=true')) {
                      console.log('[PaymentScreen] ❌ Payment ERROR detected!');
                      clearInterval(checkInterval);
                      const errorMsg = extractErrorMessageFromUrl(iframeUrl);
                      handlePaymentFailure(errorMsg);
                    }
                    // Check for success patterns
                    else if ((iframeUrl.includes('success=true') && !iframeUrl.includes('error_occured=true') && !iframeUrl.includes('DECLINED')) || 
                             (iframeUrl.includes('/success') && !iframeUrl.includes('error_occured=true') && !iframeUrl.includes('DECLINED')) ||
                             iframeUrl.includes('txn_response_code=APPROVED')) {
                      console.log('[PaymentScreen] ✅ Payment SUCCESS detected!');
                      clearInterval(checkInterval);
                      handlePaymentSuccess();
                    } 
                    // Check for other failure patterns
                    else if (iframeUrl.includes('success=false') || 
                             iframeUrl.includes('/failure') ||
                             iframeUrl.includes('/error') ||
                             iframeUrl.includes('payment-callback.html')) {
                      console.log('[PaymentScreen] ❌ Payment FAILURE detected!');
                      clearInterval(checkInterval);
                      const errorMsg = extractErrorMessageFromUrl(iframeUrl);
                      handlePaymentFailure(errorMsg);
                    }
                  } catch (error) {
                    // Cross-origin restriction - expected
                    // Paymob iframe is on different domain
                  }
                }, 1000);

                // Cleanup on unmount
                return () => clearInterval(checkInterval);
              }
            }}
            src={paymentUrl}
            style={{
              width: '100%',
              height: '100%',
              border: 'none',
              flex: 1
            }}
            title="Paymob Payment"
          />
            </View>
          </SafeAreaView>
          <MoreOptionsModal visible={showMoreModal} onClose={closeMoreMenu} />
        </>
      );
    }
    
    // For Native platforms (iOS/Android) - use WebView
    if (WebView) {
      return (
        <>
          <SafeAreaView style={styles.webViewContainer}>
            <View style={styles.webViewHeader}>
              <Logo size="sm" />
              <View style={styles.webViewHeaderActions}>
                <TouchableOpacity
                  style={styles.inlineMoreButton}
                  onPress={openMoreMenu}
                >
                  <Ionicons name="ellipsis-horizontal" size={20} color={colors.primary} />
                </TouchableOpacity>
                <TouchableOpacity
                  style={styles.cancelButton}
                  onPress={() => {
                    Alert.alert(
                      'Cancel Payment',
                      'Are you sure you want to cancel this payment?',
                      [
                        { text: 'No', style: 'cancel' },
                        { 
                          text: 'Yes', 
                          style: 'destructive',
                          onPress: () => {
                            setShowWebView(false);
                            navigateTo('subscription');
                          }
                        }
                      ]
                    );
                  }}
                >
                  <Text style={styles.cancelButtonText}>✕ Cancel</Text>
                </TouchableOpacity>
              </View>
            </View>
          
            <WebView
            source={{ uri: paymentUrl }}
            onNavigationStateChange={handleWebViewNavigationStateChange}
            onMessage={(event: any) => {
              console.log('[PaymentScreen] WebView message received:', event.nativeEvent.data);
              try {
                const data = JSON.parse(event.nativeEvent.data);
                if (data.type === 'paymob_success') {
                  console.log('[PaymentScreen] Payment success message received, clearing token and redirecting to login');
                  handlePaymentSuccess();
                } else if (data.type === 'paymob_failure') {
                  const errorMsg = data.error || data.errorMessage;
                  handlePaymentFailure(errorMsg);
                }
              } catch (e) {
                console.error('[PaymentScreen] Error parsing WebView message:', e);
              }
            }}
            startInLoadingState={true}
            renderLoading={() => (
              <View style={styles.loadingContainer}>
                <ActivityIndicator size="large" color={colors.primary} />
                <Text style={styles.loadingText}>Loading payment...</Text>
              </View>
            )}
            style={styles.webView}
          />
          </SafeAreaView>
          <MoreOptionsModal visible={showMoreModal} onClose={closeMoreMenu} />
        </>
      );
    }
  }

  // Loading screen while payment is being prepared
  if (loading) {
    return (
      <>
        <SafeAreaView style={styles.container}>
          <View style={styles.loadingContainer}>
            <Logo size="md" style={styles.logo} />
            <ActivityIndicator size="large" color={colors.primary} style={styles.spinner} />
            <Text style={styles.loadingText}>Preparing payment...</Text>
            <Text style={styles.amountText}>{amount} EGP</Text>
            {currency !== 'EGP' && (
              <Text style={styles.conversionNote}>≈ ${amount} {currency}</Text>
            )}
            {description && (
              <Text style={styles.descriptionText}>{description}</Text>
            )}
          </View>
          {renderFloatingMoreButton('light')}
        </SafeAreaView>
        <MoreOptionsModal visible={showMoreModal} onClose={closeMoreMenu} />
      </>
    );
  }

  // Fallback if something went wrong
  return (
    <>
      <SafeAreaView style={styles.container}>
        <View style={styles.errorContainer}>
          <Text style={styles.errorText}>Processing payment status</Text>
          <TouchableOpacity
            style={styles.backButton}
            onPress={() => navigateTo('subscription')}
          >
            <Text style={styles.backButtonText}>Go Back</Text>
          </TouchableOpacity>
        </View>
        {renderFloatingMoreButton('light')}
      </SafeAreaView>
      <MoreOptionsModal visible={showMoreModal} onClose={closeMoreMenu} />
    </>
  );
};

const getStyles = () => StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#fff',
    position: 'relative',
  },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: '#fff',
    padding: spacing.xl,
  },
  logo: {
    marginBottom: spacing.xl,
  },
  spinner: {
    marginVertical: spacing.md,
  },
  loadingText: {
    ...typography.h3,
    color: colors.textSecondary,
    marginTop: spacing.md,
    marginBottom: spacing.md,
  },
  amountText: {
    fontSize: 36,
    fontWeight: 'bold',
    color: colors.primary,
    marginBottom: spacing.xs,
  },
  descriptionText: {
    ...typography.body,
    color: colors.textSecondary,
    textAlign: 'center',
  },
  conversionNote: {
    ...typography.caption,
    color: colors.textSecondary,
    marginTop: spacing.xs,
    fontStyle: 'italic',
  },
  errorContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    padding: spacing.xl,
  },
  errorText: {
    ...typography.h2,
    color: colors.error,
    marginBottom: spacing.lg,
    textAlign: 'center',
  },
  backButton: {
    backgroundColor: colors.primary,
    paddingHorizontal: spacing.xl,
    paddingVertical: spacing.md,
    borderRadius: 8,
  },
  backButtonText: {
    color: '#fff',
    fontSize: 16,
    fontWeight: 'bold',
  },
  iosBackContainer: {
    padding: spacing.lg,
    paddingTop: 0,
  },
  iosBackButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: spacing.sm,
    backgroundColor: colors.primary,
    paddingVertical: spacing.md,
    paddingHorizontal: spacing.lg,
    borderRadius: spacing.borderRadius.md,
  },
  iosBackButtonText: {
    color: colors.white,
    fontSize: typography.fontSize.base,
    fontFamily: typography.fontFamily.semibold,
  },
  // WebView styles
  webViewContainer: {
    flex: 1,
    backgroundColor: '#fff',
  },
  webViewHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: spacing.md,
    borderBottomWidth: 1,
    borderBottomColor: '#eee',
    backgroundColor: '#fff',
  },
  webViewHeaderActions: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  cancelButton: {
    alignSelf: 'flex-end',
    padding: spacing.sm,
  },
  cancelButtonText: {
    color: colors.primary,
    fontSize: 16,
    fontWeight: '600',
  },
  webView: {
    flex: 1,
  },
  webIframeWrapper: {
    flex: 1,
    position: 'relative',
  },
  inlineMoreButton: {
    padding: spacing.sm,
    borderRadius: 8,
    backgroundColor: colors.background.secondary,
    marginRight: spacing.sm,
  },
  moreButton: {
    position: 'absolute',
    top: spacing.lg,
    right: spacing.lg,
    width: 36,
    height: 36,
    borderRadius: 18,
    backgroundColor: 'rgba(255,255,255,0.95)',
    alignItems: 'center',
    justifyContent: 'center',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.15,
    shadowRadius: 4,
    elevation: 4,
    zIndex: 20,
  },
  moreButtonDark: {
    backgroundColor: 'rgba(0,0,0,0.5)',
  },
});

